/**
 * Returns the help-center base path including any language prefix.
 * e.g. "/help-center" or "/de/help-center"
 */
export const getHelpCenterBase = (pathname) => {
  const path = pathname ?? (typeof window === "undefined" ? "" : window.location.pathname)
  const idx = path.indexOf("/help-center")
  return idx === -1 ? "/help-center" : path.substring(0, idx + "/help-center".length)
}

/**
 * What a help center pathname points at — `/help-center/category/<path>`,
 * `/help-center/category/<path>/article/<slug>`, `/help-center/article/<slug>`
 * (an optional /xx language prefix before any of them):
 * `{ type: "article" | "category" | null, categoryPath, articleSlug }`, all
 * null for the home and the submit-request page. Pure, so the server can
 * resolve the page it renders the same way the browser does.
 */
export const parseHelpCenterPath = (pathname = "") => {
  const none = { type: null, categoryPath: null, articleSlug: null }
  const hcIndex = pathname.indexOf("/help-center")
  if (hcIndex === -1) return none
  if (pathname.includes("/help-center/submit-request")) return none

  const segments = pathname.substring(hcIndex + "/help-center".length).split("/").filter(Boolean)
  if (segments.length >= 3 && segments[0] === "category" && segments.includes("article")) {
    const articleIndex = segments.indexOf("article")
    return {
      type: "article",
      categoryPath: segments.slice(1, articleIndex).join("/"),
      articleSlug: segments[articleIndex + 1],
    }
  }
  if (segments.length >= 2 && segments[0] === "article") {
    return { type: "article", categoryPath: null, articleSlug: segments[1] }
  }
  if (segments.length >= 2 && segments[0] === "category") {
    return { type: "category", categoryPath: segments.slice(1).join("/"), articleSlug: null }
  }
  return none
}

/**
 * Extract path information from URL - single source of truth
 * @returns {Object} Object containing path information
 */
export const getPathFromUrl = () => {
  if (typeof window === "undefined")
    return { type: null, slug: null, categoryPath: null }

  const pathString = window.location.pathname

  let contentType = null
  let slug = null
  let categoryPath = null

  if (pathString.includes("/article/")) {
    contentType = "article"

    const articleMatch = pathString.match(/\/article\/([^\/]+)(?:\/|$)/)
    if (articleMatch && articleMatch[1]) {
      slug = articleMatch[1]

      const categoryMatch = pathString.match(
        /\/category\/([^\/]+(?:\/[^\/]+)*)\/article\//
      )
      if (categoryMatch && categoryMatch[1]) {
        categoryPath = categoryMatch[1]
      }
    }
  } else if (pathString.includes("/category/")) {
    contentType = "category"

    const categoryMatch = pathString.match(
      /\/category\/([^\/]+(?:\/[^\/]+)*)(?:\/|$)/
    )
    if (categoryMatch && categoryMatch[1]) {
      categoryPath = categoryMatch[1]
      const segments = categoryPath.split("/")
      slug = segments[segments.length - 1]
    }
  }

  return {
    type: contentType,
    slug,
    categoryPath,
    exactPath:
      contentType === "article"
        ? `${categoryPath}/article/${slug}`
        : categoryPath,
    directParentPath: contentType === "article" ? categoryPath : null,
  }
}
